'use client';

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';

interface CategoryFilterContextType {
  selectedCategoryId: string | null;
  selectedCategoryName: string | null;
  setSelectedCategory: (id: string | null, name: string | null) => void;
  clearFilter: () => void;
  /** Register a reset callback (called when navigating to home via logo) */
  registerResetCallback: (callback: () => void) => void;
  /** Unregister the reset callback */
  unregisterResetCallback: () => void;
  /** Reset everything (filter + pagination) - used by Header logo click */
  resetAll: () => void;
}

const CategoryFilterContext = createContext<CategoryFilterContextType | undefined>(undefined);

export function CategoryFilterProvider({ children }: { children: ReactNode }) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null);
  const resetCallbackRef = useRef<(() => void) | null>(null);

  const setSelectedCategory = (id: string | null, name: string | null) => {
    setSelectedCategoryId(id);
    setSelectedCategoryName(name);
  };

  const clearFilter = useCallback(() => {
    setSelectedCategoryId(null);
    setSelectedCategoryName(null);
  }, []);

  const registerResetCallback = useCallback((callback: () => void) => {
    resetCallbackRef.current = callback;
  }, []);

  const unregisterResetCallback = useCallback(() => {
    resetCallbackRef.current = null;
  }, []);

  const resetAll = useCallback(() => {
    // Clear category filter
    setSelectedCategoryId(null);
    setSelectedCategoryName(null);
    // Call pagination reset if registered
    if (resetCallbackRef.current) {
      resetCallbackRef.current();
    }
  }, []);

  return (
    <CategoryFilterContext.Provider
      value={{
        selectedCategoryId,
        selectedCategoryName,
        setSelectedCategory,
        clearFilter,
        registerResetCallback,
        unregisterResetCallback,
        resetAll,
      }}
    >
      {children}
    </CategoryFilterContext.Provider>
  );
}

export function useCategoryFilter() {
  const context = useContext(CategoryFilterContext);
  if (context === undefined) {
    throw new Error('useCategoryFilter must be used within a CategoryFilterProvider');
  }
  return context;
}
